import type { GameResponse, GameState as WireGameState, GameConfig, Territory, TerritoryId, Region, MapDef } from '@warcarp/api-types'
import { reinforcementCount as sharedReinforcementCount } from '@warcarp/api-types'

/**
 * The wire `GameState` has `mapId: string` — the full MapDef is fetched
 * separately via GET /maps/:id. Client-side we compose the two into a
 * `HydratedGameState` that also carries `map: MapDef` so existing consumers
 * (phase UIs, MapView, the log screen) can access territory/region data
 * without knowing where it came from.
 *
 * The composite hook `useGameWithMap` is the canonical way to obtain a
 * HydratedGameState. Helpers below (`reinforcementCount`, etc.) accept
 * HydratedGameState so TypeScript enforces that callers have hydrated first.
 */
export type HydratedGameState = WireGameState & { map: MapDef }

/**
 * Re-export as `GameState` so existing imports of
 * `import type { GameState } from '../../lib/gameState'` continue to resolve
 * to the **hydrated** shape that includes `map: MapDef`.
 *
 * This is intentionally the hydrated type, not the wire type — every component
 * that imports GameState from this module operates on fully-hydrated state.
 * For the raw wire shape, import `GameState` directly from `@warcarp/api-types`.
 */
export type GameState = HydratedGameState

export function getGameState(game: GameResponse): WireGameState | null {
  return game.state ? (game.state as unknown as WireGameState) : null
}

/**
 * Calculate how many new armies a player receives at the start of their turn.
 *
 * Thin adapter over the shared `reinforcementCount` from `@warcarp/api-types`
 * so existing call sites that pass `(state, playerId)` don't need to change.
 * The authoritative formula lives in `packages/api-types/src/gameLogic.ts`.
 *
 * Note: the shared function returns 0 for eliminated players (zero owned
 * territories). The old local mirror was missing this guard — the shared
 * version is correct. UI call sites already guard on `eliminated` status
 * before calling this (see PlayerRosterPanel), so the behaviour is unchanged.
 *
 * Requires a HydratedGameState (with map populated) because it reads territory
 * ownership and region definitions.
 */
export function reinforcementCount(state: HydratedGameState, playerId: string): number {
  const { map } = state
  const owned = map.territories.filter((t: Territory) => t.ownerId === playerId)
  const bonusArmies = state.bonusArmies?.[playerId] ?? 0
  return sharedReinforcementCount(owned, map.regions, map.territories, bonusArmies, playerId)
}

export type { WireGameState, GameConfig, Territory, Region, MapDef, TerritoryId }
